/**
 * Small UPS draft/name/clone bindings for the descriptor engine (#997 Story 6.4,
 * mirror of `racksEngineBindings.ts`).
 *
 * The engine's generic factories (`descriptorCreateDraft` /
 * `descriptorEnsureNameForCreate` / `descriptorCloneForCreate`) don't match the
 * bespoke Small UPS CREATE-flow semantics: bespoke drafts carry
 * `DEFAULT_SMALL_UPS_CONFIGURATION` (status pre-filled to 'new'), auto-names are
 * serial hashes (`UPS-102H33`, `generateAutoSmallUpsName`), and duplicates take a
 * fresh name while dropping id/metadata. Per the story ADR (parity by reuse),
 * each binding here delegates to the PROVEN bespoke util and only converts shapes
 * at the engine boundary (`SmallUpsConfiguration` ↔ `DescriptorInstance` via the
 * Story 6.4 converters).
 */

import {
  cloneSmallUpsForCreate,
  createDraftSmallUpsConfiguration,
} from '../../../../components/smallups/smallUpsListUtils';
import {
  descriptorInstanceToSmallUps,
  smallUpsToDescriptorInstance,
} from '../../../../services/descriptorEditSessionFlush';
import { generateAutoSmallUpsName } from '../../../../utils/smallUpsAutoName';
import { SMALL_UPS_DESCRIPTOR } from '../../../sections/smallUps.descriptor';

import type { UpdateSmallUpsConfigurationInput } from '../../../../types/smallUps';
import type {
  DescriptorInstance,
  DescriptorInstancePatch,
} from '../../../sections/descriptorEngineAdapter';

function patchToSmallUpsInput(
  data: DescriptorInstancePatch,
): UpdateSmallUpsConfigurationInput {
  const { instanceName, instanceStatus, ...fields } = data;
  return {
    ...fields,
    name: typeof instanceName === 'string' ? instanceName : undefined,
    status: instanceStatus as UpdateSmallUpsConfigurationInput['status'],
  } as UpdateSmallUpsConfigurationInput;
}

/** Bespoke draft factory: DEFAULT_SMALL_UPS_CONFIGURATION + normalizeStatus. */
export function smallUpsCreateDraft(
  data: DescriptorInstancePatch,
  displayOrder: number,
  tempId: number,
): DescriptorInstance {
  const unit = createDraftSmallUpsConfiguration(
    patchToSmallUpsInput(data),
    displayOrder,
    tempId,
  );
  return smallUpsToDescriptorInstance(unit) as DescriptorInstance;
}

/** Bespoke auto-name: serial hash (`UPS-102H33`), mirrors ensureSmallUpsNameForCreate. */
export function smallUpsEnsureNameForCreate(
  data: DescriptorInstancePatch,
  tempId: number,
  takenNames: Iterable<string | undefined>,
): DescriptorInstancePatch {
  if (typeof data.instanceName === 'string' && data.instanceName.trim()) {
    return data;
  }
  return {
    ...data,
    instanceName: generateAutoSmallUpsName(tempId, takenNames),
  };
}

/** Bespoke duplicate: fresh name, keeps status, drops id/metadata. */
export function smallUpsCloneForCreate(
  source: DescriptorInstance,
  takenNames: Iterable<string | undefined>,
  tempId: number,
): Partial<DescriptorInstance> {
  const sourceUnit = descriptorInstanceToSmallUps(
    source,
    0,
    SMALL_UPS_DESCRIPTOR,
  );
  const cloned = cloneSmallUpsForCreate(sourceUnit, takenNames, tempId);
  return smallUpsToDescriptorInstance({
    ...cloned,
    id: tempId,
  } as Parameters<typeof smallUpsToDescriptorInstance>[0]) as Partial<DescriptorInstance>;
}
